/**
Project: Voice Panel (c)
Title: Routing Actions 
Description: Routing actions to get/select/enable/disable the routing policies
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/

import axios from "axios";
import { AppConfigProps } from "../../../../core/settings/app-config";
import {
  logMessage,
  checkIdentitySession,
  getSessionExpiredError,
} from "../../../../core/actions/common-actions";
import {
  VOICE_ROUTES,
  ROUTING_POLICIES,
  SELECT_ROUTING_POLICY,
  INCREMENT_VOICE_ROUTE_PRIORITY,
  DECREMENT_VOICE_ROUTE_PRIORITY,
  UPDATE_VOICE_ROUTE_PRIORITY,
  UNASSIGN_VOICE_ROUTE,
  ASSIGN_VOICE_ROUTE,
  ENABLE_VOICE_ROUTES_UPDATE_BUTTON,
  DISABLE_VOICE_ROUTES_UPDATE_BUTTON,
  ROUTING_DATA_RESET,
  SET_VOICE_ROUTES,
} from "../../../../store/voice-admin/constants";
const actionFileName = "routing-actions.js";

export function getRoutingPolicies(cancelToken) {
  return async function (dispatch) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix + "/routing-policies",
          cancelToken
        )
        .then((res) => {
          if (
            res &&
            res.status === AppConfigProps.httpStatusCode.ok &&
            res.data &&
            res.data.records
          ) {
            dispatch({
              type: ROUTING_POLICIES,
              payload: res.data.records,
            });
          } else {
            dispatch({
              type: ROUTING_POLICIES,
              payload: null,
            });
          }
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getRoutingPolicies()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            throw err && err.response ? err.response : err;
          }
        });
    } else {
      throw getSessionExpiredError();
    }
  };
}

export function selectRoutingPolicy(policyId) {
  return async function (dispatch) {
    try {
      dispatch({
        type: SELECT_ROUTING_POLICY,
        payload: policyId,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function getVoiceRoutes(policyId, cancelToken) {
  return async function (dispatch) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix +
            `/routing-policies/${policyId}/voice-routes`,
          cancelToken
        )
        .then((res) => {
          if (
            res &&
            res.status === AppConfigProps.httpStatusCode.ok &&
            res.data &&
            res.data.records
          ) {
            dispatch({
              type: VOICE_ROUTES,
              payload: res.data.records,
            });
          } else {
            dispatch({
              type: VOICE_ROUTES,
              payload: null,
            });
          }
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getVoiceRoutes()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            throw err && err.response ? err.response : err;
          }
        });
    } else {
      throw getSessionExpiredError();
    }
  };
}

export function disableRoutingPolicy(policyId, policyData, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .delete(
          AppConfigProps.serverRoutePrefix + `/routing-policies/${policyId}`,
          policyData,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">disableRoutingPolicy()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function enableRoutingPolicy(policyId, policyData, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .put(
          AppConfigProps.serverRoutePrefix + `/routing-policies/${policyId}`,
          policyData,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">enableRoutingPolicy()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function updateVoiceRoutes(policyId, routesData, cancelToken) {
  return async function (dispatch) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .put(
          AppConfigProps.serverRoutePrefix +
            `/routing-policies/${policyId}/voice-routes`,
          routesData,
          cancelToken
        )
        .then((res) => {
          if (
            res &&
            res.status === AppConfigProps.httpStatusCode.ok &&
            res.data &&
            res.data.records
          ) {
            dispatch({
              type: VOICE_ROUTES,
              payload: res.data.records,
            });
          } else {
            dispatch({
              type: VOICE_ROUTES,
              payload: null,
            });
          }
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">updateVoiceRoutes()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            throw err && err.response ? err.response : err;
          }
        });
    } else {
      throw getSessionExpiredError();
    }
  };
}

export function incrementVoiceRoutePriority(routeName) {
  return async function (dispatch) {
    try {
      dispatch({
        type: INCREMENT_VOICE_ROUTE_PRIORITY,
        payload: routeName,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function decrementVoiceRoutePriority(routeName) {
  return async function (dispatch) {
    try {
      dispatch({
        type: DECREMENT_VOICE_ROUTE_PRIORITY,
        payload: routeName,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function assignVoiceRoute(routeName) {
  return async function (dispatch) {
    try {
      dispatch({
        type: ASSIGN_VOICE_ROUTE,
        payload: routeName,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function unassignVoiceRoute(routeName) {
  return async function (dispatch) {
    try {
      dispatch({
        type: UNASSIGN_VOICE_ROUTE,
        payload: routeName,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function updateVoiceRoutePriority({ routeName, newPriorityValue }) {
  return async function (dispatch) {
    try {
      dispatch({
        type: UPDATE_VOICE_ROUTE_PRIORITY,
        payload: { routeName, newPriorityValue },
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function enableVoiceRoutesUpdateButton() {
  return async function (dispatch) {
    try {
      dispatch({
        type: ENABLE_VOICE_ROUTES_UPDATE_BUTTON,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function disableVoiceRoutesUpdateButton() {
  return async function (dispatch) {
    try {
      dispatch({
        type: DISABLE_VOICE_ROUTES_UPDATE_BUTTON,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function resetRoutingData() {
  return async function (dispatch) {
    try {
      dispatch({
        type: ROUTING_DATA_RESET,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function setVoiceRoutes(voiceRoutes) {
  return async function (dispatch) {
    try {
      dispatch({
        type: SET_VOICE_ROUTES,
        payload: voiceRoutes,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}
